// Firebase Analytics Data Fetching Utility
import { collection, query, where, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getAnalyticsMetrics } from './firestoreMetrics';

// Fetch real error events from Firestore
const fetchErrorEvents = async (dateRange = null) => {
  try {
    let q = query(
      collection(db, 'analytics'),
      where('event', '==', 'error_occurred'),
      orderBy('timestamp', 'desc')
    );

    if (dateRange && dateRange.start && dateRange.end) {
      q = query(
        collection(db, 'analytics'),
        where('event', '==', 'error_occurred'),
        where('timestamp', '>=', dateRange.start.toISOString()),
        where('timestamp', '<=', dateRange.end.toISOString()),
        orderBy('timestamp', 'desc')
      );
    }

    const querySnapshot = await getDocs(q);
    const errorEvents = [];
    const errorCounts = {};

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const errorType = data.error_type || 'Unknown Error';
      const errorMessage = data.error_message || 'No message';
      const timestamp = data.timestamp;

      if (!errorCounts[errorType]) {
        errorCounts[errorType] = {
          count: 0,
          lastOccurred: timestamp,
          message: errorMessage
        };
      }

      errorCounts[errorType].count++;
      if (new Date(timestamp) > new Date(errorCounts[errorType].lastOccurred)) {
        errorCounts[errorType].lastOccurred = timestamp;
      }
    });

    // Convert to array format
    Object.entries(errorCounts).forEach(([errorType, data]) => {
      errorEvents.push({
        error: errorType,
        count: data.count,
        lastOccurred: new Date(data.lastOccurred).toISOString().split('T')[0],
        message: data.message
      });
    });

    // Sort by count (highest first)
    return errorEvents.sort((a, b) => b.count - a.count);

  } catch (error) {
    console.error('Error fetching error events:', error);
    return [];
  }
};

// Fetch real analytics data from Firestore
export const fetchAnalyticsData = async (dateRange = null) => {
  try {
    console.log('Fetching analytics data from Firestore metrics...');
    
    // Use the new Firestore metrics system
    const result = await getAnalyticsMetrics(dateRange);
    
    if (result.success) {
      // Fetch real error events
      const realErrorEvents = await fetchErrorEvents(dateRange);
      
      // Add additional data that might be needed
      const analyticsData = {
        ...result.data,
        // Add top pages data (simulated based on quotes)
        topPages: [
          { page: '/', views: Math.round(result.data.quotesSubmitted * 8), key: 'home' },
          { page: '/QuotePage', views: result.data.quotesSubmitted, key: 'quote' },
          { page: '/services', views: Math.round(result.data.quotesSubmitted * 3), key: 'services' },
          { page: '/about', views: Math.round(result.data.quotesSubmitted * 2), key: 'about' },
          { page: '/contact', views: Math.round(result.data.quotesSubmitted * 1.5), key: 'contact' }
        ],
        // Use real error events data
        errorEvents: realErrorEvents
      };

      return {
        success: true,
        data: analyticsData,
        dateRange: dateRange
      };
    } else {
      return result;
    }

  } catch (error) {
    console.error('Error fetching analytics data:', error);
    return {
      success: false,
      error: error.message,
      data: getFallbackData()
    };
  }
};

// Calculate analytics metrics from quotes data
const calculateAnalyticsMetrics = async (quotes, startDate, endDate) => {
  const totalQuotes = quotes.length;
  const uniqueEmails = new Set(quotes.map(quote => quote.email)).size;
  
  // Calculate conversion rate (quotes per unique user)
  const conversionRate = uniqueEmails > 0 ? (totalQuotes / uniqueEmails) * 100 : 0;

  // Group quotes by date for page views simulation
  const quotesByDate = {};
  quotes.forEach(quote => {
    const date = quote.createdAt.toISOString().split('T')[0];
    quotesByDate[date] = (quotesByDate[date] || 0) + 1;
  });

  // Generate daily page views (estimate based on quotes)
  const dailyViews = [];
  const currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    const dateStr = currentDate.toISOString().split('T')[0];
    const quotesCount = quotesByDate[dateStr] || 0;
    // Estimate page views as 5-10x the number of quotes
    const estimatedViews = quotesCount * (5 + Math.random() * 5);
    
    dailyViews.push({
      date: dateStr,
      views: Math.round(estimatedViews),
      quotes: quotesCount
    });
    
    currentDate.setDate(currentDate.getDate() + 1);
  }

  // Calculate campaign data from quotes
  const campaignData = calculateCampaignData(quotes);

  // Get top services
  const serviceCounts = {};
  quotes.forEach(quote => {
    if (quote.selectedServices) {
      Object.entries(quote.selectedServices).forEach(([serviceId, isSelected]) => {
        if (isSelected) {
          serviceCounts[serviceId] = (serviceCounts[serviceId] || 0) + 1;
        }
      });
    }
  });

  const topServices = Object.entries(serviceCounts)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10)
    .map(([serviceId, count]) => ({
      service: serviceId,
      count: count,
      percentage: ((count / totalQuotes) * 100).toFixed(1)
    }));

  // Generate top pages data (simulated based on quotes)
  const topPages = [
    { page: '/', views: Math.round(totalQuotes * 8), key: 'home' },
    { page: '/QuotePage', views: totalQuotes, key: 'quote' },
    { page: '/services', views: Math.round(totalQuotes * 3), key: 'services' },
    { page: '/about', views: Math.round(totalQuotes * 2), key: 'about' },
    { page: '/contact', views: Math.round(totalQuotes * 1.5), key: 'contact' }
  ];

  // Fetch real error events
  const errorEvents = await fetchErrorEvents({ start: startDate, end: endDate });

  return {
    totalPageViews: dailyViews.reduce((sum, day) => sum + day.views, 0),
    uniqueUsers: uniqueEmails,
    quotesSubmitted: totalQuotes,
    conversionRate: parseFloat(conversionRate.toFixed(2)),
    dailyViews: dailyViews,
    campaignData: campaignData,
    topServices: topServices,
    topPages: topPages,
    errorEvents: errorEvents,
    recentQuotes: quotes.slice(0, 10).map(quote => ({
      id: quote.id,
      email: quote.email,
      address: quote.address,
      services: Object.keys(quote.selectedServices || {}).filter(key => quote.selectedServices[key]),
      createdAt: quote.createdAt,
      amount: quote.quoteAmount || 0
    }))
  };
};

// Calculate campaign data from quotes
const calculateCampaignData = (quotes) => {
  const campaigns = {
    'free-inspect-weed': { 
      title: 'Free Inspection and Weed Pulling', 
      visits: 0, 
      quotes: 0, 
      revenue: 0,
      campaign: 'free-inspect-weed'
    },
    '25-off-6-months': { 
      title: '25% Off 6 month plan', 
      visits: 0, 
      quotes: 0, 
      revenue: 0,
      campaign: '25-off-6-months'
    },
    '30-off-3-months': { 
      title: '30% Off 3 month plan', 
      visits: 0, 
      quotes: 0, 
      revenue: 0,
      campaign: '30-off-3-months'
    }
  };

  // Calculate real campaign data from quotes with UTM data
  quotes.forEach(quote => {
    const campaignKey = quote.utmCampaign?.campaign;
    
    if (campaignKey && campaigns[campaignKey]) {
      campaigns[campaignKey].quotes += 1;
      campaigns[campaignKey].revenue += quote.quoteAmount || 500;
      // Estimate visits as 5-10x the number of quotes for each campaign
      campaigns[campaignKey].visits += Math.floor(Math.random() * 5) + 5;
    }
  });

  // Calculate conversion rates
  Object.keys(campaigns).forEach(campaignKey => {
    const campaign = campaigns[campaignKey];
    campaign.conversionRate = campaign.visits > 0 ? 
      parseFloat(((campaign.quotes / campaign.visits) * 100).toFixed(2)) : 0;
  });

  return campaigns;
};

// Fallback data when Firebase is not available
const getFallbackData = () => {
  return {
    totalPageViews: 0,
    uniqueUsers: 0,
    quotesSubmitted: 0,
    conversionRate: 0,
    dailyViews: [],
    campaignData: {
      'free-inspect-weed': { title: 'Free Inspection and Weed Pulling', visits: 0, quotes: 0, conversionRate: 0, revenue: 0, campaign: 'free-inspect-weed' },
      '25-off-6-months': { title: '25% Off 6 month plan', visits: 0, quotes: 0, conversionRate: 0, revenue: 0, campaign: '25-off-6-months' },
      '30-off-3-months': { title: '33% Off 3 month plan', visits: 0, quotes: 0, conversionRate: 0, revenue: 0, campaign: '30-off-3-months' }
    },
    topServices: [],
    topPages: [],
    errorEvents: [],
    recentQuotes: []
  };
};

// Fetch real-time analytics data
export const fetchRealTimeAnalytics = async () => {
  try {
    // Get quotes from the last 24 hours
    const last24Hours = new Date(Date.now() - 24 * 60 * 60 * 1000);
    
    const quotesQuery = query(
      collection(db, 'quotes'),
      where('createdAt', '>=', last24Hours),
      orderBy('createdAt', 'desc')
    );

    const quotesSnapshot = await getDocs(quotesQuery);
    const recentQuotes = [];
    quotesSnapshot.forEach((doc) => {
      recentQuotes.push({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      });
    });

    return {
      success: true,
      recentQuotes: recentQuotes,
      totalToday: recentQuotes.length
    };

  } catch (error) {
    console.error('Error fetching real-time analytics:', error);
    return {
      success: false,
      error: error.message,
      recentQuotes: [],
      totalToday: 0
    };
  }
};
